import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import SettingsIcon from "@material-ui/icons/Settings";
import { computePivot, rangoFechasRegistro, resolveFieldValue } from "../../../../../../services/pivotEngine";
import { timestampToDate } from "../../../../../../services/utils";
import DashboardDateRangePicker from "../DashboardDateRangePicker";
import PivotResultTable from "./PivotResultTable";
import PivotResultChart from "./PivotResultChart";

// Valores distintos que de verdad existen en los inscritos para un campo —
// así el filtro es un selector de valores reales (sin typos / sin que el
// organizador tenga que adivinar cómo está guardado el dato), en vez de un
// campo de texto libre.
function valoresDistintos(eventParticipants, fieldId, registryAutorizedUsers) {
  if (!fieldId) return [];
  const vistos = new Set();
  // eventParticipants es un objeto keyed por id (no array) — ver
  // registry-reducer.js, mismo criterio que pivotEngine.computePivot.
  Object.values(eventParticipants || {}).forEach((p) => {
    if (p && !p.borrado && p.statusPago && p.statusPago.status === "ok") {
      vistos.add(resolveFieldValue(p, fieldId, registryAutorizedUsers));
    }
  });
  return Array.from(vistos).sort();
}

// El valor crudo de "modalidad" es solo el código (ej. "1") y el de
// "usuarioRegistra" es un uid — ninguno dice nada por sí solo en una
// tabla/gráfico. Se traducen al nombre real (modalidad vía
// eventSubEventsRegistry, usuario vía registryAutorizedUsers — mismo
// criterio que ya usa DashboardRegistryByUser.jsx) tanto para las opciones
// del filtro como para las filas/columnas del resultado. El valor que se
// guarda/filtra sigue siendo el crudo — solo cambia lo que se MUESTRA.
function traducirValor(eventSubEventsRegistry, registryAutorizedUsers, fieldId, valorCrudo) {
  if (fieldId === "modalidad" && eventSubEventsRegistry) {
    const modalidad = eventSubEventsRegistry["modalidad_" + valorCrudo];
    if (modalidad && modalidad.descripcionModalidad) {
      return modalidad.descripcionModalidad;
    }
  }
  if (fieldId === "usuarioRegistra" && registryAutorizedUsers) {
    const usuario = registryAutorizedUsers[valorCrudo];
    if (usuario && usuario.nombre) {
      return usuario.nombre;
    }
  }
  return valorCrudo;
}

function traducirResultado(result, rowField, colField, eventSubEventsRegistry, registryAutorizedUsers) {
  const trRow = (v) => traducirValor(eventSubEventsRegistry, registryAutorizedUsers, rowField, v);
  const trCol = (v) => traducirValor(eventSubEventsRegistry, registryAutorizedUsers, colField, v);

  const rows = result.rows.map(trRow);
  const cols = result.cols.map(trCol);

  const matrix = {};
  result.rows.forEach((r) => {
    const filaTraducida = {};
    Object.keys(result.matrix[r] || {}).forEach((c) => {
      filaTraducida[trCol(c)] = result.matrix[r][c];
    });
    matrix[trRow(r)] = filaTraducida;
  });

  const rowTotals = {};
  Object.keys(result.rowTotals).forEach((r) => {
    rowTotals[trRow(r)] = result.rowTotals[r];
  });
  const colTotals = {};
  Object.keys(result.colTotals).forEach((c) => {
    colTotals[trCol(c)] = result.colTotals[c];
  });

  return { rows, cols, matrix, rowTotals, colTotals, grandTotal: result.grandTotal };
}

export default function PivotReportBuilder({
  camposDisponibles,
  eventParticipants,
  eventSubEventsRegistry,
  registryAutorizedUsers,
  initialConfig,
  onGuardarComo,
  onActualizar,
  onEliminar,
  guardando,
  puedeEditar,
}) {
  const esExistente = Boolean(initialConfig && initialConfig.id);

  // Oculto por defecto para un reporte ya guardado (lo que se quiere ver es
  // el resultado, no el formulario) — abierto por defecto al crear uno
  // nuevo, porque ahí no hay nada que mostrar todavía hasta configurarlo.
  // Un usuario que solo puede VER nunca lo abre (ni tiene el botón para
  // hacerlo), así que para él se queda cerrado siempre.
  const [formularioAbierto, setFormularioAbierto] = useState(puedeEditar && !esExistente);

  const [name, setName] = useState((initialConfig && initialConfig.name) || "");
  const [rowField, setRowField] = useState((initialConfig && initialConfig.rowField) || "");
  const [usarColumnas, setUsarColumnas] = useState(Boolean(initialConfig && initialConfig.colField));
  const [colField, setColField] = useState((initialConfig && initialConfig.colField) || "");
  const [filters, setFilters] = useState((initialConfig && initialConfig.filters) || []);
  const [dateFrom, setDateFrom] = useState((initialConfig && initialConfig.dateFrom) || "");
  const [dateTo, setDateTo] = useState((initialConfig && initialConfig.dateTo) || "");
  const [aggregation, setAggregation] = useState((initialConfig && initialConfig.aggregation) || "count");
  const [verTabla, setVerTabla] = useState(true);
  const [verGrafico, setVerGrafico] = useState(true);

  const etiquetaPorCampo = useMemo(() => {
    const mapa = {};
    camposDisponibles.forEach((c) => (mapa[c.iDCampo] = c.etiqueta));
    return mapa;
  }, [camposDisponibles]);

  const { min: minFechaRegistro, max: maxFechaRegistro } = useMemo(
    () => rangoFechasRegistro(eventParticipants),
    [eventParticipants]
  );

  function agregarFiltro() {
    setFilters([...filters, { field: "", value: "" }]);
  }
  function cambiarFiltro(idx, campo) {
    const nuevos = [...filters];
    nuevos[idx] = { ...nuevos[idx], ...campo };
    setFilters(nuevos);
  }
  function quitarFiltro(idx) {
    setFilters(filters.filter((_, i) => i !== idx));
  }

  const config = {
    rowField,
    colField: usarColumnas ? colField : null,
    filters: filters.filter((f) => f.field && f.value),
    dateFrom: dateFrom || null,
    dateTo: dateTo || null,
    aggregation,
    registryAutorizedUsers,
  };

  const result = useMemo(
    () => computePivot(eventParticipants, config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventParticipants, rowField, usarColumnas, colField, filters, dateFrom, dateTo, aggregation, registryAutorizedUsers]
  );

  const resultTraducido = useMemo(
    () =>
      traducirResultado(
        result,
        rowField,
        usarColumnas ? colField : null,
        eventSubEventsRegistry,
        registryAutorizedUsers
      ),
    [result, rowField, usarColumnas, colField, eventSubEventsRegistry, registryAutorizedUsers]
  );

  function guardar() {
    const payload = {
      name,
      rowField,
      colField: usarColumnas ? colField : null,
      filters: config.filters,
      dateFrom: config.dateFrom,
      dateTo: config.dateTo,
      aggregation,
    };
    if (esExistente) {
      onActualizar(payload);
    } else {
      onGuardarComo(payload);
    }
  }

  return (
    <Box>
      {puedeEditar && (
        <Button
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => setFormularioAbierto(!formularioAbierto)}
        >
          {formularioAbierto
            ? "Ocultar configuración"
            : esExistente
            ? "Editar este reporte"
            : "Configurar nuevo reporte"}
        </Button>
      )}

      <Collapse in={puedeEditar && formularioAbierto}>
      <Grid container spacing={2} style={{ marginTop: 8 }}>
        {esExistente && (initialConfig.createdBy || (initialConfig.modifiedBy && initialConfig.modifiedBy.length > 0)) && (
          <Grid item xs={12}>
            <Box style={{ padding: 8, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
              {initialConfig.createdBy && (
                <Typography variant="caption" display="block" color="textSecondary">
                  Creado por {initialConfig.createdBy.nombre}
                  {initialConfig.createdAt ? ` el ${timestampToDate(initialConfig.createdAt)}` : ""}
                </Typography>
              )}
              {initialConfig.modifiedBy && initialConfig.modifiedBy.length > 0 && (
                <Typography variant="caption" display="block" color="textSecondary">
                  Modificado por{" "}
                  {initialConfig.modifiedBy
                    .slice()
                    .reverse()
                    .map((m) => `${m.nombre} (${timestampToDate(m.date)})`)
                    .join(", ")}
                </Typography>
              )}
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            label="Nombre del reporte"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            select
            label="Agrupar filas por"
            fullWidth
            value={rowField}
            onChange={(e) => setRowField(e.target.value)}
          >
            {camposDisponibles.map((c) => (
              <MenuItem key={c.iDCampo} value={c.iDCampo}>
                {c.etiqueta}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={usarColumnas}
                onChange={(e) => setUsarColumnas(e.target.checked)}
              />
            }
            label="Agrupar también por columnas (tabla cruzada)"
          />
        </Grid>
        {usarColumnas && (
          <Grid item xs={12}>
            <TextField
              select
              label="Agrupar columnas por"
              fullWidth
              value={colField}
              onChange={(e) => setColField(e.target.value)}
            >
              {camposDisponibles
                .filter((c) => c.iDCampo !== rowField)
                .map((c) => (
                  <MenuItem key={c.iDCampo} value={c.iDCampo}>
                    {c.etiqueta}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="subtitle2">Filtros (opcional)</Typography>
        </Grid>
        {filters.map((f, idx) => (
          <Grid item xs={12} key={idx} container spacing={1} alignItems="center">
            <Grid item xs={5}>
              <TextField
                select
                label="Campo"
                fullWidth
                value={f.field}
                onChange={(e) => cambiarFiltro(idx, { field: e.target.value, value: "" })}
              >
                {camposDisponibles.map((c) => (
                  <MenuItem key={c.iDCampo} value={c.iDCampo}>
                    {c.etiqueta}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={5}>
              <TextField
                select
                label="Valor"
                fullWidth
                disabled={!f.field}
                value={f.value}
                onChange={(e) => cambiarFiltro(idx, { value: e.target.value })}
              >
                {valoresDistintos(eventParticipants, f.field, registryAutorizedUsers).map((v) => (
                  <MenuItem key={v} value={v}>
                    {traducirValor(eventSubEventsRegistry, registryAutorizedUsers, f.field, v)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={2}>
              <IconButton onClick={() => quitarFiltro(idx)} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Button size="small" startIcon={<AddIcon />} onClick={agregarFiltro}>
            Agregar filtro
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Rango de fecha de inscripción (opcional)
          </Typography>
          <DashboardDateRangePicker
            minDate={minFechaRegistro}
            maxDate={maxFechaRegistro}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChangeDateFrom={setDateFrom}
            onChangeDateTo={setDateTo}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            select
            label="¿Qué quieres ver en cada celda?"
            fullWidth
            value={aggregation}
            onChange={(e) => setAggregation(e.target.value)}
          >
            <MenuItem value="count">Cantidad de inscritos</MenuItem>
            <MenuItem value="sum">Suma de dinero cobrado</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={verTabla} onChange={(e) => setVerTabla(e.target.checked)} />}
            label="Ver tabla"
          />
          <FormControlLabel
            control={<Checkbox checked={verGrafico} onChange={(e) => setVerGrafico(e.target.checked)} />}
            label="Ver gráfico"
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            disabled={!rowField || !name.trim() || guardando}
            onClick={guardar}
            style={{ marginRight: 8 }}
          >
            {esExistente ? "Actualizar" : "Guardar como nuevo reporte"}
          </Button>
          {esExistente && (
            <Button
              color="secondary"
              disabled={guardando}
              onClick={onEliminar}
            >
              Eliminar
            </Button>
          )}
        </Grid>
      </Grid>
      </Collapse>

      {rowField && (
        <Box mt={3}>
          {verTabla && (
            <Box mb={2}>
              <PivotResultTable
                result={resultTraducido}
                rowLabel={etiquetaPorCampo[rowField]}
                colLabel={usarColumnas ? etiquetaPorCampo[colField] : null}
                aggregation={aggregation}
              />
            </Box>
          )}
          {verGrafico && <PivotResultChart result={resultTraducido} aggregation={aggregation} />}
        </Box>
      )}
    </Box>
  );
}
